import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Activity, AlertTriangle, CheckCircle, Clock, RefreshCw, Crown } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Progress } from '../components/ui/progress';
// import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { useMonitoring } from '../hooks/useMonitoring';
import { useTier } from '../contexts/TierContext';
import { useSpace } from '../context/SpaceContext';
import { useAnalytics } from '../hooks/useAnalytics';
import { useTranslation } from 'react-i18next';
import { formatDistanceToNow } from 'date-fns';
import { MemoryReadHealthPanel } from '../components/MemoryReadHealthPanel';
import apiService from '../services/api';
import { getIntegrationDisplayName } from '../integrations/registry';
import type {
  MemoryReadSummaryResponse,
  MemoryReadTimeRange,
} from '../services/memoryReadSummary';
import { buildBillingUpgradeUrl } from '../utils/billingNavigation';

/** Maps monitoring hour window to API `time_range` (no new backend shape). */
function memoryReadRangeFromHours(hours: number): MemoryReadTimeRange {
  if (hours <= 24) return '7d';
  if (hours <= 168) return '30d';
  return '90d';
}

const MONITORING_UI_TIERS = new Set(['starter', 'pro', 'enterprise']);

// Type definition for integration metrics (reference)
// @ts-expect-error - Type definition for reference purposes
interface _IntegrationMetricsRef {
  time_window: { start: string; end: string; hours: number };
  summary: {
    total_events_24h: number;
    success_rate: number;
    avg_processing_time: number;
    active_alerts: number;
  };
  historical: {
    total_events: number;
    success_rate: number;
    failure_rate: number;
    avg_processing_time_ms: number;
    status_breakdown: Record<string, number>;
  };
}

// Type definition for integration health (used for type reference)
// @ts-expect-error - Type definition for reference purposes
interface _IntegrationHealth {
  integrations: Record<string, {
    health_score: number;
    status: 'healthy' | 'degraded' | 'unhealthy';
    last_success: string | null;
    failure_rate: number;
    avg_response_time: number;
  }>;
  overall_health: {
    score: number;
    status: 'healthy' | 'degraded' | 'unhealthy';
  };
}

interface Alert {
  type: string;
  source: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  message: string;
  failure_rate?: number;
  threshold?: number;
  avg_time_ms?: number;
}

// Mock data for preview (when user doesn't have access)
const mockIntegrationMetrics = {
  time_window: { start: '2026-01-01', end: '2026-01-02', hours: 24 },
  summary: { total_events_24h: 1247, success_rate: 0.987, avg_processing_time: 142, active_alerts: 2 },
  historical: {
    total_events: 8934,
    success_rate: 0.991,
    failure_rate: 0.009,
    avg_processing_time_ms: 138,
    status_breakdown: { success: 8856, client_error: 30, server_error: 15, auth_error: 8, rate_limited: 5 },
  },
  real_time: {},
};

const mockIntegrationHealth = {
  integrations: {
    whatsapp_basic: { health_score: 98.5, status: 'healthy' as const, last_success: '2026-01-01T10:30:00Z', failure_rate: 0.015, avg_response_time: 125 },
    n8n: { health_score: 99.0, status: 'healthy' as const, last_success: '2026-01-01T10:28:00Z', failure_rate: 0.01, avg_response_time: 95 },
    custom_gpt_action: { health_score: 97.2, status: 'healthy' as const, last_success: '2026-01-01T10:32:00Z', failure_rate: 0.028, avg_response_time: 140 },
  },
  overall_health: { score: 98.5, status: 'healthy' as const },
  window_hours: 24,
  window_label: 'last_24h',
  last_updated: '2026-01-01T12:00:00',
};

const mockAlerts = {
  total_count: 2,
  alerts: [
    { type: 'performance', source: 'whatsapp_basic', severity: 'medium' as const, message: 'Response time increased in the last hour' },
    { type: 'reliability', source: 'n8n', severity: 'low' as const, message: 'Elevated non-success outcomes on workflow triggers' },
  ],
  severity_breakdown: { low: 1, medium: 1, high: 0, critical: 0 },
};

type IntegrationHealthRow = {
  health_score: number;
  status: 'healthy' | 'degraded' | 'unhealthy';
  last_success: string | null;
  failure_rate: number;
  non_success_rate?: number;
  server_error_rate?: number;
  avg_response_time: number;
};

const emptyIntegrationMetrics = {
  time_window: { start: '', end: '', hours: 24 },
  summary: { total_events_24h: 0, success_rate: 1, avg_processing_time: 0, active_alerts: 0 },
  historical: {
    total_events: 0,
    success_rate: 1,
    failure_rate: 0,
    avg_processing_time_ms: 0,
    status_breakdown: {} as Record<string, number>,
  },
  real_time: {} as Record<string, unknown>,
};

const emptyIntegrationHealth = {
  integrations: {} as Record<string, IntegrationHealthRow>,
  overall_health: { score: 0, status: 'unknown' as const },
  window_hours: 1,
  window_label: 'last_1h',
  last_updated: '',
};

const emptyAlerts = {
  total_count: 0,
  alerts: [] as typeof mockAlerts.alerts,
  severity_breakdown: {} as Record<string, number>,
};

const MONITORING_TIME_WINDOW_SCOPE_ID = 'monitoring-time-window-scope';

const MonitoringPage: React.FC = () => {
  const navigate = useNavigate();
  const [timeWindow, setTimeWindow] = useState(24);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState<NodeJS.Timeout | null>(null);
  const { tierInfo } = useTier();
  const { currentSpace } = useSpace();
  const spaceIdForApi = currentSpace?.space_id;
  const { t } = useTranslation();
  const { trackEvent } = useAnalytics();
  const [memoryReadSummary, setMemoryReadSummary] =
    useState<MemoryReadSummaryResponse | null>(null);
  const [memoryReadLoading, setMemoryReadLoading] = useState(false);
  const [memoryReadError, setMemoryReadError] = useState<string | null>(null);
  const memoryReadLoadIdRef = useRef(0);

  const tierSlug = tierInfo?.current_tier?.tier?.toLowerCase() ?? '';
  const hasMonitoringAccess = MONITORING_UI_TIERS.has(tierSlug);
  const monitoringFetchEnabled =
    hasMonitoringAccess && Boolean(spaceIdForApi);

  const {
    integrationMetrics: realIntegrationMetrics,
    integrationHealth: realIntegrationHealth,
    alerts: realAlerts,
    isLoading,
    error,
    refetch
  } = useMonitoring(
    monitoringFetchEnabled ? timeWindow : 0,
    monitoringFetchEnabled ? spaceIdForApi : undefined
  );

  const integrationMetrics = !hasMonitoringAccess
    ? mockIntegrationMetrics
    : monitoringFetchEnabled
      ? (realIntegrationMetrics ?? emptyIntegrationMetrics)
      : emptyIntegrationMetrics;
  const integrationHealth = !hasMonitoringAccess
    ? mockIntegrationHealth
    : monitoringFetchEnabled
      ? (realIntegrationHealth ?? emptyIntegrationHealth)
      : emptyIntegrationHealth;
  const alerts = !hasMonitoringAccess
    ? mockAlerts
    : monitoringFetchEnabled
      ? (realAlerts ?? emptyAlerts)
      : emptyAlerts;

  useEffect(() => {
    if (autoRefresh && monitoringFetchEnabled) {
      const interval = setInterval(() => {
        refetch();
      }, 30000); // Refresh every 30 seconds
      setRefreshInterval(interval);
      return () => clearInterval(interval);
    } else {
      if (refreshInterval) {
        clearInterval(refreshInterval);
        setRefreshInterval(null);
      }
    }
  }, [autoRefresh, monitoringFetchEnabled, refetch]);

  useEffect(() => {
    if (!hasMonitoringAccess || !spaceIdForApi) {
      setMemoryReadSummary(null);
      setMemoryReadLoading(false);
      setMemoryReadError(null);
      return;
    }
    const req = ++memoryReadLoadIdRef.current;
    setMemoryReadLoading(true);
    setMemoryReadError(null);
    const timeRange = memoryReadRangeFromHours(timeWindow);
    void apiService
      .getMemoryReadSummary(timeRange, spaceIdForApi)
      .then((data) => {
        if (memoryReadLoadIdRef.current !== req) return;
        setMemoryReadSummary(data);
        setMemoryReadError(null);
        trackEvent('memory_read_summary_loaded', {
          time_range: timeRange,
          total_reads: data.total_reads ?? 0,
          source: 'monitoring',
        });
      })
      .catch((err: unknown) => {
        if (memoryReadLoadIdRef.current !== req) return;
        setMemoryReadSummary(null);
        setMemoryReadError(
          err instanceof Error
            ? err.message
            : 'Failed to load memory read summary'
        );
      })
      .finally(() => {
        if (memoryReadLoadIdRef.current !== req) return;
        setMemoryReadLoading(false);
      });
  }, [hasMonitoringAccess, spaceIdForApi, timeWindow, trackEvent]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy': return 'text-green-600 bg-green-50';
      case 'degraded': return 'text-yellow-600 bg-yellow-50';
      case 'unhealthy': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'destructive';
      case 'high': return 'destructive';
      case 'medium': return 'secondary';
      case 'low': return 'outline';
      default: return 'outline';
    }
  };

  const formatPercentage = (value: number) => `${(value * 100).toFixed(1)}%`;
  const formatMs = (value: number) => `${value.toFixed(0)}ms`;
  const memoryReadTimeRange = memoryReadRangeFromHours(timeWindow);
  const memoryReadWindowLabel = t(`monitoring.panelWindows.memoryRead.${memoryReadTimeRange}`, {
    ns: 'dashboard',
  });
  const integrationMetricsHours =
    typeof integrationMetrics?.time_window?.hours === 'number' &&
    integrationMetrics.time_window.hours > 0
      ? integrationMetrics.time_window.hours
      : timeWindow;
  const integrationMetricsRelativeLabel = t(
    'monitoring.panelWindows.integrationMetrics.hours',
    { ns: 'dashboard', count: integrationMetricsHours }
  );
  // Fixed 1h evaluation window in get_alert_conditions / _get_slow_integrations (integration_monitoring_service).
  const alertsWindowLabel = t('monitoring.panelWindows.alerts.windowLastHour', {
    ns: 'dashboard',
  });
  const alertsEvaluationNote = t('monitoring.panelWindows.alerts.evaluationNote', {
    ns: 'dashboard',
    window: alertsWindowLabel,
  });
  const integrationHealthWindowLabel = integrationHealth?.window_hours
    ? t('monitoring.panelWindows.integrationHealth.hours', {
        ns: 'dashboard',
        count: integrationHealth.window_hours,
      })
    : t('monitoring.panelWindows.integrationHealth.unknown', { ns: 'dashboard' });
  const memoryReadEffectiveDescription = t('monitoring.panelWindows.memoryRead.label', {
    ns: 'dashboard',
    window: memoryReadWindowLabel,
  });

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Alert className="mb-6">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            {t('monitoring.error.loadFailed', { error, ns: 'dashboard' })}
          </AlertDescription>
        </Alert>
        <Button onClick={refetch} className="mt-4">
          <RefreshCw className="w-4 h-4 mr-2" />
          {t('retry', { ns: 'common' })}
        </Button>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 relative">
      {hasMonitoringAccess && !spaceIdForApi && (
        <Alert className="mb-6">
          <AlertDescription>
            {t('monitoring.selectSpaceHint', { ns: 'dashboard' })}
          </AlertDescription>
        </Alert>
      )}
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('monitoring.title', { ns: 'dashboard' })}</h1>
          <p className="text-gray-600 mt-2">
            {t('monitoring.subtitle', { ns: 'dashboard' })}
          </p>
        </div>
        
        <div className="flex flex-col items-end gap-2 max-w-md">
          <div className="flex flex-wrap items-center justify-end gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium" id="monitoring-auto-refresh-label">
                {t('monitoring.autoRefresh', { ns: 'dashboard' })}
              </span>
              <Button
                variant={autoRefresh ? "default" : "outline"}
                size="sm"
                onClick={() => setAutoRefresh(!autoRefresh)}
                aria-labelledby="monitoring-auto-refresh-label"
              >
                <Activity className="w-4 h-4 mr-1" aria-hidden />
                {autoRefresh ? t('on', { ns: 'common' }) : t('off', { ns: 'common' })}
              </Button>
            </div>
            <div className="flex flex-col gap-1 items-end">
              <label
                htmlFor="monitoring-time-window-select"
                className="text-sm font-medium"
              >
                {t('monitoring.timeWindow.rangeLabel', { ns: 'dashboard' })}
              </label>
              <select
                id="monitoring-time-window-select"
                value={timeWindow}
                onChange={(e) => setTimeWindow(Number(e.target.value))}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                aria-describedby={MONITORING_TIME_WINDOW_SCOPE_ID}
              >
                <option value={1}>{t('monitoring.timeWindow.lastHour', { ns: 'dashboard' })}</option>
                <option value={6}>{t('monitoring.timeWindow.last6Hours', { ns: 'dashboard' })}</option>
                <option value={24}>{t('monitoring.timeWindow.last24Hours', { ns: 'dashboard' })}</option>
                <option value={168}>{t('monitoring.timeWindow.lastWeek', { ns: 'dashboard' })}</option>
              </select>
            </div>
            <Button onClick={refetch} disabled={isLoading} size="sm">
              <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} aria-hidden />
              {t('refresh', { ns: 'common' })}
            </Button>
          </div>
          <p
            id={MONITORING_TIME_WINDOW_SCOPE_ID}
            className="text-xs text-muted-foreground text-right leading-snug"
          >
            {t('monitoring.timeWindow.appliesToIntegrationMetrics', { ns: 'dashboard' })}
          </p>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('monitoring.overview.overallHealth', { ns: 'dashboard' })}</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {integrationHealth?.overall_health?.score?.toFixed(0) || '0'}%
            </div>
            <Badge className={getStatusColor(integrationHealth?.overall_health?.status || 'unknown')}>
              {integrationHealth?.overall_health?.status || t('unknown', { ns: 'common' })}
            </Badge>
            <p className="text-xs text-muted-foreground mt-2">
              {t('monitoring.overview.effectiveDataWindow', {
                ns: 'dashboard',
                window: integrationHealthWindowLabel,
              })}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('monitoring.overview.successRate', { ns: 'dashboard' })}</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatPercentage(integrationMetrics?.summary?.success_rate || 0)}
            </div>
            <p className="text-xs text-muted-foreground">
              {t('overview.eventsInTimeWindow', {
                ns: 'dashboard',
                count: integrationMetrics?.summary?.total_events_24h || 0,
                hours: integrationMetricsHours,
              })}
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              {t('monitoring.overview.effectiveDataWindow', {
                ns: 'dashboard',
                window: integrationMetricsRelativeLabel,
              })}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('monitoring.overview.avgResponseTime', { ns: 'dashboard' })}</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatMs(integrationMetrics?.summary?.avg_processing_time || 0)}
            </div>
            <p className="text-xs text-muted-foreground">
              {t('monitoring.overview.processingTime', { ns: 'dashboard' })}
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              {t('monitoring.overview.effectiveDataWindow', {
                ns: 'dashboard',
                window: integrationMetricsRelativeLabel,
              })}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('monitoring.overview.activeAlerts', { ns: 'dashboard' })}</CardTitle>
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {alerts?.total_count || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              {t('monitoring.overview.monitoringAlerts', { ns: 'dashboard' })}
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              {t('monitoring.overview.effectiveDataWindow', {
                ns: 'dashboard',
                window: alertsWindowLabel,
              })}
            </p>
          </CardContent>
        </Card>
      </div>

      {hasMonitoringAccess && (
        <div className="mb-8">
          <MemoryReadHealthPanel
            summary={memoryReadSummary}
            loading={memoryReadLoading}
            error={memoryReadError}
            effectiveWindowDescription={memoryReadEffectiveDescription}
          />
        </div>
      )}

      {/* Main Content Tabs */}
      <Tabs defaultValue="integrations" className="space-y-6">
        <TabsList>
          <TabsTrigger value="integrations">{t('monitoring.tabs.integrationHealth', { ns: 'dashboard' })}</TabsTrigger>
          <TabsTrigger value="integration-requests">{t('monitoring.tabs.integrationRequests', { ns: 'dashboard' })}</TabsTrigger>
          <TabsTrigger value="alerts">{t('monitoring.tabs.alerts', { ns: 'dashboard' })}</TabsTrigger>
        </TabsList>

        {/* Integration Health Tab */}
        <TabsContent value="integrations" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>{t('integrationHealth.title', { ns: 'dashboard' })}</CardTitle>
              <CardDescription>
                {t('integrationHealth.description', { ns: 'dashboard' })}
              </CardDescription>
              <p className="text-xs text-muted-foreground">
                {t('monitoring.panelWindows.integrationHealth.label', {
                  ns: 'dashboard',
                  window: integrationHealthWindowLabel,
                })}
              </p>
              <p className="text-xs text-muted-foreground">
                {t('monitoring.panelWindows.integrationHealth.help', { ns: 'dashboard' })}
              </p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {integrationHealth?.integrations && Object.entries(integrationHealth.integrations).map(([source, health]) => (
                  <Card key={source} className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold">{getIntegrationDisplayName(source)}</h3>
                      <Badge className={getStatusColor(health.status)}>
                        {health.status}
                      </Badge>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>{t('integrationHealth.healthScore', { ns: 'dashboard' })}</span>
                        <span className="font-medium">{health.health_score.toFixed(0)}%</span>
                      </div>
                      <Progress value={health.health_score} className="h-2" />
                      
                      <div className="grid grid-cols-3 gap-2 text-xs text-gray-600 mt-3">
                        <div>
                          <span className="block">{t('integrationHealth.reliabilityErrorRate', { ns: 'dashboard' })}</span>
                          <span className="font-medium">{formatPercentage(health.server_error_rate ?? 0)}</span>
                        </div>
                        <div>
                          <span className="block">{t('integrationHealth.nonSuccessRate', { ns: 'dashboard' })}</span>
                          <span className="font-medium">{formatPercentage(health.non_success_rate ?? health.failure_rate)}</span>
                        </div>
                        <div>
                          <span className="block">{t('integrationHealth.avgResponse', { ns: 'dashboard' })}</span>
                          <span className="font-medium">{formatMs(health.avg_response_time)}</span>
                        </div>
                      </div>
                      
                      {health.last_success && (
                        <p className="text-xs text-gray-500 mt-2">
                          {t('integrationHealth.lastSuccess', {
                            ns: 'dashboard',
                            time: formatDistanceToNow(new Date(health.last_success), { addSuffix: true }),
                          })}
                        </p>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Integration request outcomes */}
        <TabsContent value="integration-requests" className="space-y-6">
          {/* Charts removed - will be re-enabled in future update */}

          {/* Status Breakdown */}
          {integrationMetrics?.historical?.status_breakdown && (
            <Card>
              <CardHeader>
                <CardTitle>{t('monitoring.integrationRequests.outcomeBreakdownTitle', { ns: 'dashboard' })}</CardTitle>
                <CardDescription>
                  {t('monitoring.integrationRequests.outcomeBreakdownDescription', { ns: 'dashboard' })}
                </CardDescription>
                <CardDescription className="text-xs">
                  {t('monitoring.panelWindows.integrationMetrics.outcomeBreakdownWindow', {
                    ns: 'dashboard',
                    window: integrationMetricsRelativeLabel,
                  })}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {Object.entries(integrationMetrics.historical.status_breakdown).map(([status, count]) => (
                    <div key={status} className="text-center p-4 bg-gray-50 rounded-lg">
                      <div className="text-2xl font-bold">{count}</div>
                      <div className="text-sm text-gray-600 capitalize">{status.replace('_', ' ')}</div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Alerts Tab */}
        <TabsContent value="alerts" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>{t('alerts.activeAlerts', { ns: 'dashboard' })}</CardTitle>
              <CardDescription>
                {t('alerts.description', { ns: 'dashboard' })}
              </CardDescription>
              <CardDescription className="text-xs">{alertsEvaluationNote}</CardDescription>
            </CardHeader>
            <CardContent>
              {alerts?.alerts && alerts.alerts.length > 0 ? (
                <div className="space-y-4">
                  {alerts.alerts.map((alert: Alert, index: number) => (
                    <Alert key={index} className="border-l-4 border-l-red-500">
                      <AlertTriangle className="h-4 w-4" />
                      <div className="flex items-center justify-between w-full">
                        <div>
                          <AlertDescription className="font-medium">
                            {alert.message}
                          </AlertDescription>
                          <p className="text-xs text-gray-500 mt-1">
                            {t('alerts.alertDetails', {
                              ns: 'dashboard',
                              source: getIntegrationDisplayName(alert.source),
                              type: alert.type,
                            })}
                          </p>
                        </div>
                        <Badge variant={getSeverityColor(alert.severity)}>
                          {alert.severity}
                        </Badge>
                      </div>
                    </Alert>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    {t('alerts.noActiveAlerts', { ns: 'dashboard' })}
                  </h3>
                  <p className="text-gray-600">{t('alerts.allOperatingNormally', { ns: 'dashboard' })}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Alert Summary */}
          {alerts?.severity_breakdown && Object.keys(alerts.severity_breakdown).length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>{t('alerts.severityBreakdown', { ns: 'dashboard' })}</CardTitle>
                <CardDescription className="text-xs">{alertsEvaluationNote}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {Object.entries(alerts.severity_breakdown).map(([severity, count]) => (
                    <div key={severity} className="text-center p-4 bg-gray-50 rounded-lg">
                      <div className="text-2xl font-bold">{count}</div>
                      <div className="text-sm text-gray-600 capitalize">{severity}</div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Performance tab removed: charts/summary were placeholder-heavy; re-enable when monitoring charts ship. */}
      </Tabs>
      
      {/* Starter+ access gate - Blur Overlay */}
      {!hasMonitoringAccess && (
        <div className="absolute inset-0 bg-card/80 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="rounded-xl border border-border bg-card p-8 shadow-2xl max-w-md mx-4">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <Activity className="w-8 h-8 text-white" />
              </div>
              
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                {t('proAccess.title', { ns: 'dashboard' })}
              </h3>
              
              <p className="text-gray-600 mb-6 leading-relaxed">
                {t('proAccess.description', { ns: 'dashboard' })}
              </p>
              
              <div className="space-y-3 mb-6 text-left">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-sm text-gray-700">{t('proAccess.features.realTimeTracking', { ns: 'dashboard' })}</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-sm text-gray-700">{t('proAccess.features.healthScoring', { ns: 'dashboard' })}</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-sm text-gray-700">{t('proAccess.features.performanceAnalytics', { ns: 'dashboard' })}</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-sm text-gray-700">{t('proAccess.features.autoRefresh', { ns: 'dashboard' })}</span>
                </div>
              </div>
              
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-4 mb-6">
                <p className="text-sm text-gray-700">
                  <span className="font-semibold">{t('proAccess.currentPlan', { ns: 'dashboard' })}:</span> {tierInfo?.current_tier?.tier || t('tiers.free', { ns: 'common' })}
                </p>
                <p className="text-sm text-blue-600 font-medium mt-1">
                  {t('proAccess.upgradeMessage', { ns: 'dashboard' })}
                </p>
              </div>
              
              <Button 
                className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-medium"
                onClick={() =>
                  navigate(
                    buildBillingUpgradeUrl({
                      returnPath: '/dashboard?view=monitoring',
                      source: 'monitoring',
                    })
                  )
                }
              >
                <Crown className="w-4 h-4 mr-2" />
                {t('proAccess.upgradeButton', { ns: 'dashboard' })}
              </Button>
              
              <p className="text-xs text-gray-500 mt-4">
                {t('proAccess.guarantee', { ns: 'dashboard' })}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MonitoringPage;
